import axios from 'axios';
import { getApiHost } from 'src/utils/ssoReferrer';

export interface DivisionMembership {
  id: string;
  name: string;
  companyName: string;
  role: string;
  appAccess: Record<string, string>;
  isActive: boolean;
}

export interface V2LoginResponse {
  userId: string;
  email: string;
  name: string;
  divisions: DivisionMembership[];
  lastActiveDivisionId: string;
  sessionToken: string;
}

export interface V2TokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  divisionId: string;
  divisionName: string;
  // Optional so the frontend tolerates older backends mid-deploy
  // and falls back to a plain reload(). Populated when the division's
  // VendorId resolves to a positive int, otherwise omitted.
  defaultBeaconClientId?: number;
}

export interface V2ValidateResponse {
  authenticated: boolean;
  reason?: string;
  user?: {
    userId: string;
    email: string;
    name: string;
  };
  division?: DivisionMembership;
  divisions?: DivisionMembership[];
  sessionToken?: string;
}

/**
 * Lightweight liveness response. Returned by GET /v2/session-info.
 * Used by tab-focus / mismatch hooks that only need to know whether the
 * cookie's bound division has changed -- no DB lookup, no Redis write.
 */
export interface V2SessionInfoResponse {
  authenticated: boolean;
  reason?: string;
  divisionId?: string;
}

/**
 * One-shot session token response. Returned by POST /v2/session-token.
 * Used by the cross-app SSO no-access redirect flow that bridges into
 * POST /v2/token for a different division.
 */
export interface V2SessionTokenResponse {
  sessionToken: string;
  expiresIn: number;
}

export const v2LoginFederated = async (
  accessToken: string,
  idToken: string,
  referrer?: string
): Promise<V2LoginResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/login/federated`, {
    accessToken,
    idToken,
    referrer
  });
  return response.data;
};

export const v2IssueToken = async (
  sessionToken: string,
  divisionId: string,
  referrer?: string
): Promise<V2TokenResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/token`, {
    sessionToken,
    divisionId,
    referrer
  }, {
    withCredentials: true
  });
  return response.data;
};

export const v2GetDivisions = async (): Promise<{
  divisions: DivisionMembership[];
  activeDivisionId: string;
}> => {
  const baseUrl = getApiHost();
  const response = await axios.get(`https://${baseUrl}/api/auth/v2/divisions`);
  return response.data;
};

/**
 * POST /v2/switch-division -- atomic in-place division switch.
 * Single round trip; server revokes the old JTI and sets the new
 * sl-auth-token cookie via Set-Cookie. Returns the new bearer token plus
 * `defaultBeaconClientId` for /client/:id URL rewrites.
 *
 * Replaces the legacy v2Validate -> v2IssueToken pair which required two
 * round trips and didn't revoke the old token.
 *
 * `referrer` is the originating app (e.g. "atlas", "omni-dev"). When provided
 * AND the new division has non-blocked appAccess for that app, the server
 * updates the per-app last-active key in Redis so a future login via the same
 * app prefers this division as the default. Optional but recommended -- omit
 * to skip the per-app write while still performing the switch.
 */
export const v2SwitchDivision = async (
  newDivisionId: string,
  referrer?: string
): Promise<V2TokenResponse & { previousTokenRevoked: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/switch-division`, {
    newDivisionId,
    referrer
  }, {
    withCredentials: true
  });
  return response.data;
};

/**
 * POST /v2/validate -- DEPRECATED. Bundles liveness + divisions list +
 * sessionToken into one heavy call. Prefer:
 *   - v2SessionInfo()      for liveness + active divisionId (focus checks)
 *   - v2GetDivisions()     for the divisions list
 *   - v2SessionToken()     for the cross-app SSO redirect flow
 *   - v2SwitchDivision()   for in-place division switching
 *
 * The sessionToken field is now opt-in via ?includeSessionToken=true on the
 * server, so calling without the flag returns null in that field.
 */
export const v2Validate = async (): Promise<V2ValidateResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/validate`, {}, {
    withCredentials: true
  });
  return response.data;
};

/**
 * GET /v2/session-info -- lightweight liveness check.
 * Returns {authenticated, divisionId} from JWT verify + revocation check.
 * No DB lookup, no Redis write. Used by tab-focus and mismatch hooks.
 *
 * Error-handling contract: only a definitive HTTP 401 from the server is
 * surfaced as `{authenticated: false}`. Every other failure (5xx, network
 * error, CORS preflight rejection, axios-retry exhaustion) is *re-thrown*
 * so that callers can distinguish "the server told us the session is
 * dead" (must redirect to sign-in) from "we couldn't ask the server"
 * (transient -- leave the user where they are and try again next focus).
 *
 * Previously this method caught everything and returned
 * `{authenticated: false}`, which made `useDivisionCheck` redirect users
 * to `signin.stackline.com` on any network blip -- the root cause of the
 * "I got logged out for no reason" reports paired with the Sentry
 * `[AxiosRetry] Request failed after maximum retries` alerts on this
 * exact URL.
 */
export const v2SessionInfo = async (): Promise<V2SessionInfoResponse> => {
  const baseUrl = getApiHost();
  try {
    const response = await axios.get(`https://${baseUrl}/api/auth/v2/session-info`, {
      withCredentials: true
    });
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 401) {
      return { authenticated: false, reason: 'unauthorized' };
    }
    throw err;
  }
};

/**
 * POST /v2/session-token -- mint a one-shot session token for the
 * authenticated user. Used by the cross-app SSO no-access redirect flow that
 * bridges into POST /v2/token. Replaces the SessionToken side-effect that
 * used to be bundled into /v2/validate.
 */
export const v2SessionToken = async (): Promise<V2SessionTokenResponse | null> => {
  const baseUrl = getApiHost();
  try {
    const response = await axios.post(`https://${baseUrl}/api/auth/v2/session-token`, {}, {
      withCredentials: true
    });
    return response.data;
  } catch {
    return null;
  }
};

export const v2Logout = async (): Promise<{ success: boolean }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/logout`);
  return response.data;
};

/**
 * POST /v2/refresh -- exchange the HttpOnly `sl-refresh-token` cookie
 * for a fresh `sl-auth-token` cookie (rotated server-side via Set-Cookie).
 *
 * No arguments: the refresh token lives in the `sl-refresh-token` cookie,
 * and the divisionId + email are read by the server from the
 * (possibly expired) `sl-auth-token` cookie. `withCredentials: true` is
 * required so both cookies actually travel. Sends an empty body to satisfy
 * the controller's `[FromBody] RefreshTokenRequest` -- the cookie path
 * takes precedence over body, so a missing `refreshToken` field is fine.
 *
 * Throws (non-2xx) when the refresh chain has been broken (cookie missing,
 * Cognito revoked the refresh token, etc). Callers must redirect to
 * sign-in on failure.
 */
export const v2Refresh = async (): Promise<V2TokenResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/refresh`, {}, {
    withCredentials: true
  });
  return response.data;
};

/**
 * POST /v2/otp/send -- request a one-time code be emailed via Cognito CUSTOM_AUTH.
 *
 * The response includes `challengeSession`, an opaque Cognito Session blob that
 * the caller MUST thread back into v2OtpVerify. Storing it in component state
 * (rather than relying on the server-side Redis cache the backend used to
 * maintain) makes the OTP flow immune to admin cache purges between send and
 * verify. The Session is signed by Cognito, single-use, and ~3-minute TTL'd
 * server-side -- the same trust model AWS Amplify uses when it stores the
 * Session client-side between InitiateAuth and RespondToAuthChallenge.
 */
export const v2OtpSend = async (
  email: string
): Promise<{ success: boolean; challengeSession?: string }> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/otp/send`, { email });
  return response.data;
};

/**
 * POST /v2/otp/verify -- verify OTP code and return divisions + session token.
 *
 * `challengeSession` is the opaque Cognito CUSTOM_AUTH Session string returned
 * from v2OtpSend. Passing it back here bypasses the (cache-clearable) Redis
 * entry the backend keeps for older clients. Optional during the rollout
 * window -- omitting it makes the backend fall back to its Redis cache, which
 * still works but is vulnerable to admin cache purges between send and verify.
 *
 * `referrer` is the originating app (e.g. "atlas", "omni-dev"). When provided
 * the response's `lastActiveDivisionId` prefers a division with that app's
 * access (per-app Redis key first, then newest membership in the capable set).
 * Optional but recommended -- omitting it keeps the legacy 3-tier global
 * fallback (Redis global last-active -> newest -> sl-users.divisionId).
 */
export const v2OtpVerify = async (
  email: string,
  code: string,
  challengeSession?: string,
  referrer?: string
): Promise<V2LoginResponse> => {
  const baseUrl = getApiHost();
  const response = await axios.post(`https://${baseUrl}/api/auth/v2/otp/verify`, {
    email,
    code,
    challengeSession,
    referrer
  });
  return response.data;
};

/**
 * Pick the default division for auto-login.
 * Backend already resolves lastActiveDivisionId with 3-tier fallback
 * (Redis last-active -> sl-users.divisionId -> oldest membership)
 * and returns divisions sorted by createdAt ascending.
 */
export function resolveDefaultDivision(
  divisions: DivisionMembership[],
  lastActiveDivisionId: string | undefined
): DivisionMembership {
  if (lastActiveDivisionId) {
    const preferred = divisions.find(d => d.id === lastActiveDivisionId);
    if (preferred) return preferred;
  }
  return divisions[0];
}

export const fetchSubscriptions = async (accessToken: string, appName: string): Promise<any> => {
  const baseUrl = getApiHost();
  const response = await axios.get(`https://${baseUrl}/api/auth/v2/subscriptions`, {
    params: { appName },
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });
  return response.data;
};
